import React, { useEffect, useRef, useState } from 'react';
import useAuth from '../../hooks/useAuth';

const getVideoMimeType = (url: string): string => {
  const ext = url.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/)?.[1]?.toLowerCase() ?? 'mp4';
  const map: Record<string, string> = {
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    webm: 'video/webm',
    avi: 'video/x-msvideo',
    mkv: 'video/x-matroska',
  };
  return map[ext] ?? 'video/mp4';
};

export interface AuthenticatedVideoProps {
  /** Full URL to the video asset (often requires Authorization to fetch). */
  fileUrl: string;
  mimeType?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Loads video via fetch with the admin Bearer token and plays from a blob URL.
 * Plain &lt;video src={fileUrl}&gt; cannot send auth headers, so protected URLs fail to play.
 */
const AuthenticatedVideo: React.FC<AuthenticatedVideoProps> = ({ fileUrl, mimeType, className, style }) => {
  const { authToken } = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'error' | 'ready'>('loading');
  const blobRef = useRef<string | null>(null);

  useEffect(() => {
    if (!fileUrl) {
      setBlobUrl(null);
      setLoadState('loading');
      return;
    }

    let cancelled = false;

    const revoke = () => {
      if (blobRef.current) {
        URL.revokeObjectURL(blobRef.current);
        blobRef.current = null;
      }
    };

    revoke();
    setBlobUrl(null);
    setLoadState('loading');

    (async () => {
      try {
        const token = authToken || localStorage.getItem('authorization_token');
        const response = await fetch(fileUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobRef.current = url;
        setBlobUrl(url);
        setLoadState('ready');
      } catch {
        if (!cancelled) {
          setLoadState('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      revoke();
    };
  }, [fileUrl, authToken]);

  const type = mimeType ?? getVideoMimeType(fileUrl);

  if (loadState === 'error') {
    return (
      <p className="mt-5 flex justify-center bg-gray-700 p-2 text-sm text-white">
        Could not load video preview. The file may still download correctly — use Download below.
      </p>
    );
  }

  if (loadState !== 'ready' || !blobUrl) {
    return <p className="mt-5 flex justify-center bg-gray-700 py-2 text-sm text-white">Loading video…</p>;
  }

  return (
    <video key={blobUrl} controls className={className} style={style}>
      <source src={blobUrl} type={type} />
      Your browser does not support the video tag.
    </video>
  );
};

export default AuthenticatedVideo;
